import { Box, Button, Center, HStack, Spinner, Text } from '@chakra-ui/react'
import { QueryDocumentSnapshot } from 'firebase/firestore'
import { useRef } from 'react'
import AZSelector from '../../../../components/AZSelector'
import UsersTable from '../../../../components/UsersTable'
import FirestoreUser from '../../../../types/FirestoreUser'
import { Filter } from '../types'
import { ReportInfo } from '../../../../types/ReportTypes'
import ColorLegend from './ColorLegend'

interface Props {
  docs: QueryDocumentSnapshot<FirestoreUser>[]
  filter: Filter | undefined
  hasMore: boolean
  loading: 'more' | 'initial' | false
  reports: Record<string, ReportInfo>
  isTableLoading: boolean
  list?: boolean
  onLoadMore: () => void
  onCopyEmails: () => void
}

const firstNameFilters: Filter[] = ['firstName', 'alphaFirst']

export default function UsersList({
  docs,
  filter,
  hasMore,
  loading,
  reports,
  isTableLoading,
  list,
  onLoadMore,
  onCopyEmails,
}: Props) {
  const usersTableRef = useRef<any>(null)

  if (isTableLoading) {
    return (
      <Center py={10}>
        <Spinner size='xl' color='blue.500' thickness='4px' />
      </Center>
    )
  }

  if (docs.length === 0) {
    return null
  }

  return (
    <>
      <HStack spacing={2}>
        <Text>Total Displayed: {docs.length}</Text>
        <Button onClick={onCopyEmails}>Copy Emails</Button>
        {/* Colour legend when listing alphabetically by name */}
        <ColorLegend
          visible={
            list === true && (filter === 'alphaFirst' || filter === 'alphaLast')
          }
        />
      </HStack>

      {/* A-Z Links for scrolling (First Name, Last Name, or Email) */}
      {list && docs.length > 0 && (
        <AZSelector
          docs={docs}
          field={
            filter === 'alphaFirst'
              ? 'firstName'
              : filter === 'alphaEmail'
              ? 'email'
              : 'lastName'
          }
          hasMore={hasMore}
          showFieldLabel={filter === 'alphaEmail'}
          onLetterClick={(letter: string) => {
            if (usersTableRef.current) {
              const field =
                filter === 'alphaFirst'
                  ? 'firstName'
                  : filter === 'alphaEmail'
                  ? 'email'
                  : 'lastName'
              usersTableRef.current.scrollToInitial(
                letter,
                field === 'email' ? 'email' : field
              )
            }
          }}
        />
      )}

      <UsersTable
        ref={usersTableRef}
        docs={docs}
        lastNameFirst={filter && !firstNameFilters.includes(filter)}
        reports={reports}
        isLoading={false}
        emailFirst={filter === 'alphaEmail'}
      />

      {hasMore && !(filter === 'alphaFirst' || filter === 'alphaLast') && (
        <Box mt={5}>
          <Button
            isLoading={loading === 'more'}
            onClick={onLoadMore}
            colorScheme='blue'
          >
            Load More
          </Button>
        </Box>
      )}

      {hasMore && (filter === 'alphaFirst' || filter === 'alphaLast') && (
        <Box mt={3}>
          <Text fontSize='sm' color='gray.500'>
            Loading more users...
          </Text>
        </Box>
      )}
    </>
  )
}
